import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, ExternalLink, CheckCircle, AlertCircle, XCircle, 
  Server, Database, Cloud, User, Users, Calendar, Shield, Clock,
  FileText, AlertTriangle, ChevronDown, ChevronUp, Copy, Check,
  Activity, Tag, Zap, Timer, Eye, ThumbsUp, ThumbsDown
} from 'lucide-react';

const API_BASE = 'https://zaz9nsak09.execute-api.us-west-2.amazonaws.com/prod';

const InfraChangeDetail = ({ changeNumber, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    implementation: true,
    backout: false,
    test: false,
    cis: true,
    raw: false
  });
  const [copiedJson, setCopiedJson] = useState(false);

  useEffect(() => {
    const fetchChangeDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/infra-changes/${changeNumber}`);
        if (!response.ok) throw new Error(`API returned ${response.status}`);
        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error('[InfraChangeDetail] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (changeNumber) fetchChangeDetail();
  }, [changeNumber]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const copyJson = () => {
    if (data?.full_change_data) {
      navigator.clipboard.writeText(JSON.stringify(data.full_change_data, null, 2));
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 2000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return 'N/A'; }
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return null;
    try {
      const diff = new Date(end) - new Date(start);
      const hours = Math.floor(diff / 36e5);
      const mins = Math.floor((diff % 36e5) / 60000);
      if (hours > 24) return `${Math.floor(hours/24)}d ${hours%24}h`;
      return `${hours}h ${mins}m`;
    } catch { return null; }
  };

  const getStatusColor = (state) => {
    const colors = {
      'Approved': 'bg-green-500/20 text-green-400 border-green-500/40',
      'Implementation Authorized': 'bg-blue-500/20 text-blue-400 border-blue-500/40',
      'Pending': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      'Emergency': 'bg-red-500/20 text-red-400 border-red-500/40',
      'Closed': 'bg-gray-500/20 text-gray-400 border-gray-500/40',
      'Closed Complete': 'bg-gray-500/20 text-gray-400 border-gray-500/40',
      'In Progress': 'bg-orange-500/20 text-orange-400 border-orange-500/40'
    };
    return colors[state] || 'bg-gray-500/20 text-gray-400 border-gray-500/40';
  };

  const getRiskColor = (risk) => {
    if (!risk) return 'text-gray-400';
    const r = risk.toLowerCase();
    if (r.includes('high')) return 'text-red-400';
    if (r.includes('medium') || r.includes('moderate')) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getMatchStatusIcon = (matchResult, feedbackStatus) => {
    if (feedbackStatus === 'correct') return <CheckCircle size={16} className="text-green-500" title="Confirmed Match" />;
    if (feedbackStatus === 'incorrect') return <XCircle size={16} className="text-red-500" title="Incorrect Match" />;
    if (matchResult === 'MATCHED') return <CheckCircle size={16} className="text-cyan-accent" title="Matched" />;
    if (matchResult === 'CANDIDATES_FOUND') return <CheckCircle size={16} className="text-cyan-accent/70" title="Candidates Found" />;
    if (matchResult === 'NO_MATCH') return <AlertCircle size={16} className="text-yellow-500" title="No Match Found" />;
    if (matchResult === 'NOT_MONITORABLE') return <AlertCircle size={16} className="text-gray-400" title="Not Monitorable" />;
    return <AlertCircle size={16} className="text-gray-500" title="Pending" />;
  };

  const getConfidenceColor = (score) => {
    if (!score) return 'text-gray-500';
    if (score >= 0.9) return 'text-green-400';
    if (score >= 0.7) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getCIIcon = (ciClass) => {
    const cls = (ciClass || '').toLowerCase();
    if (cls.includes('database') || cls.includes('db')) return <Database size={16} className="text-pink-500" />;
    if (cls.includes('cloud') || cls.includes('aws') || cls.includes('azure')) return <Cloud size={16} className="text-orange-accent" />;
    return <Server size={16} className="text-purple-accent" />;
  };

  const getEnvironmentColor = (env) => {
    if (!env) return 'bg-gray-500/20 text-gray-400';
    const e = env.toLowerCase();
    if (e.includes('prod')) return 'bg-red-500/20 text-red-400';
    if (e.includes('dev')) return 'bg-blue-500/20 text-blue-400';
    if (e.includes('test') || e.includes('qa')) return 'bg-yellow-500/20 text-yellow-400';
    return 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-dark-card rounded-lg border border-dark-border p-12 text-center">
          <Activity size={32} className="animate-spin text-purple-accent mx-auto mb-4" />
          <p className="text-gray-400">Loading change details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="flex items-center gap-2 mb-4 text-purple-accent hover:text-purple-300">
          <ArrowLeft size={16} /> Back to List
        </button>
        <div className="bg-dark-card rounded-lg border border-red-500/40 p-8 text-center">
          <AlertTriangle size={32} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-2">Error loading change details</p>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { change, cis } = data;
  const duration = calculateDuration(change.start_date, change.end_date);
  const ciTotal = change.ci_total_count || 0;
  const ciMatched = change.ci_matched_count || 0;
  const ciMatchPercent = ciTotal > 0 ? Math.round((ciMatched / ciTotal) * 100) : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      <button onClick={onBack} className="flex items-center gap-2 text-purple-accent hover:text-purple-300 transition-colors">
        <ArrowLeft size={16} /> Back to List
      </button>

      {/* Main Layout: Left Panel (70%) + Right Panel (30%) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Panel - Change Details */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Change Header */}
          <div className="bg-dark-card rounded-lg border border-dark-border p-6" style={{ background: '#0d0d0d' }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-purple-accent" style={{ textShadow: '0 0 20px #b24bf3' }}>
                    {change.change_number}
                  </h1>
                  {change.change_type === 'Emergency' && (
                    <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/40 flex items-center gap-1">
                      <Zap size={12} /> Emergency
                    </span>
                  )}
                </div>
                <p className="text-lg text-white">{change.short_description}</p>
              </div>
              {change.change_sys_id && (
                <a
                  href={`https://dish.service-now.com/nav_to.do?uri=change_request.do?sys_id=${change.change_sys_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 rounded bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent hover:bg-cyan-accent/30 flex items-center gap-2 text-sm"
                >
                  <ExternalLink size={14} /> ServiceNow
                </a>
              )}
            </div>

            {/* Status Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className={`px-3 py-1 text-sm rounded border ${getStatusColor(change.change_state)}`}>
                {change.change_state || 'Unknown'}
              </span>
              <span className={`px-3 py-1 text-sm rounded border ${getStatusColor(change.approval_state)}`}>
                {change.approval_state || 'N/A'}
              </span>
              <span className={`px-3 py-1 text-sm rounded border bg-dark-border/40 border-dark-border/60 ${getRiskColor(change.risk)}`}>
                <Shield size={12} className="inline mr-1" />
                {change.risk || 'N/A'}
              </span>
            </div>

            {/* Key Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-dark-border/50">
              <InfoItem icon={User} label="Assigned To" value={change.assigned_to} />
              <InfoItem icon={Users} label="Assignment Group" value={change.assignment_group} />
              <InfoItem icon={Tag} label="Category" value={change.category} />
              <InfoItem icon={Activity} label="Impact" value={change.impact} />
            </div>

            {/* Schedule */}
            <div className="mt-4 pt-4 border-t border-dark-border/50">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={16} className="text-gray-500" />
                <span className="text-sm text-gray-400">Schedule</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-white">{formatDate(change.start_date)}</span>
                <span className="text-gray-500">→</span>
                <span className="text-white">{formatDate(change.end_date)}</span>
                {duration && (
                  <span className="px-2 py-0.5 rounded bg-purple-accent/20 text-purple-accent text-xs">
                    <Timer size={12} className="inline mr-1" />{duration}
                  </span>
                )}
              </div>
              {change.downtime && change.downtime !== 'No' && (
                <div className="mt-2 px-3 py-2 rounded bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm flex items-center gap-2">
                  <AlertTriangle size={14} />
                  Downtime: {change.downtime_start ? `${formatDate(change.downtime_start)} - ${formatDate(change.downtime_end)}` : 'Yes'}
                </div>
              )}
            </div>
          </div>

          {/* Implementation Plan */}
          <CollapsibleSection
            title="Implementation Plan"
            icon={FileText}
            expanded={expandedSections.implementation}
            onToggle={() => toggleSection('implementation')}
          >
            <div className="text-sm text-gray-300 whitespace-pre-wrap">
              {change.implementation_plan || 'No implementation plan provided.'}
            </div>
          </CollapsibleSection>

          {/* Backout Plan */}
          <CollapsibleSection
            title="Backout Plan"
            icon={AlertTriangle}
            expanded={expandedSections.backout}
            onToggle={() => toggleSection('backout')}
            highlight="orange"
          >
            <div className="text-sm text-gray-300 whitespace-pre-wrap bg-orange-500/5 p-4 rounded border border-orange-500/20">
              {change.backout_plan || 'No backout plan provided.'}
            </div>
          </CollapsibleSection>

          {/* Test Plan */}
          {change.test_plan && (
            <CollapsibleSection
              title="Test Plan"
              icon={CheckCircle}
              expanded={expandedSections.test}
              onToggle={() => toggleSection('test')}
            >
              <div className="text-sm text-gray-300 whitespace-pre-wrap">
                {change.test_plan}
              </div>
            </CollapsibleSection>
          )}

          {/* Configuration Items */}
          <CollapsibleSection
            title={`Configuration Items (${cis?.length || 0})`}
            icon={Database}
            expanded={expandedSections.cis}
            onToggle={() => toggleSection('cis')}
            badge={change.ci_needs_review_count > 0 ? `${change.ci_needs_review_count} need review` : null}
          >
            <CITable cis={cis} getCIIcon={getCIIcon} getEnvironmentColor={getEnvironmentColor} getConfidenceColor={getConfidenceColor} getMatchStatusIcon={getMatchStatusIcon} />
          </CollapsibleSection>

          {/* Raw JSON Data */}
          <CollapsibleSection
            title="Raw Data (Debug)"
            icon={FileText}
            expanded={expandedSections.raw}
            onToggle={() => toggleSection('raw')}
          >
            <div className="relative">
              <button
                onClick={copyJson}
                className="absolute top-2 right-2 px-2 py-1 rounded bg-dark-border/60 text-gray-400 hover:text-white text-xs flex items-center gap-1"
              >
                {copiedJson ? <Check size={12} /> : <Copy size={12} />}
                {copiedJson ? 'Copied!' : 'Copy'}
              </button>
              <pre className="text-xs text-gray-400 overflow-x-auto p-4 bg-dark-border/20 rounded max-h-96">
                {JSON.stringify(data.full_change_data, null, 2)}
              </pre>
            </div>
          </CollapsibleSection>
        </div>

        {/* Right Panel - Summary & Activity */}
        <div className="space-y-6">
          
          {/* CI Matching Summary */}
          <div className="bg-dark-card rounded-lg border border-dark-border p-4" style={{ background: '#0d0d0d' }}>
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Database size={16} className="text-purple-accent" />
              CI Matching Status
            </h3>
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>{ciMatched} matched</span>
                <span>{ciMatchPercent}%</span>
              </div>
              <div className="h-2 bg-dark-border/60 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    ciMatchPercent >= 90 ? 'bg-green-500' : ciMatchPercent >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${ciMatchPercent}%` }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-2 text-sm">
              <StatRow label="Total CIs" value={ciTotal} color="text-white" />
              <StatRow label="Matched" value={ciMatched} color="text-green-400" />
              <StatRow label="Needs Review" value={change.ci_needs_review_count || 0} color="text-yellow-400" />
              <StatRow label="No Match" value={change.ci_no_match_count || 0} color="text-red-400" />
              <StatRow label="Not Monitorable" value={change.ci_not_monitorable_count || 0} color="text-gray-400" />
            </div>

            {change.ci_matching_status && (
              <div className="mt-4 pt-4 border-t border-dark-border/50 text-xs text-gray-500">
                <p>Status: {change.ci_matching_status}</p>
                {change.ci_matching_completed_at && (
                  <p>Completed: {formatDate(change.ci_matching_completed_at)}</p>
                )}
              </div>
            )}
          </div>

          {/* Quick Info */}
          <div className="bg-dark-card rounded-lg border border-dark-border p-4" style={{ background: '#0d0d0d' }}>
            <h3 className="text-sm font-semibold text-white mb-4">Quick Info</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-gray-500">Opened By</span>
                <p className="text-gray-300">{change.opened_by || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Opened At</span>
                <p className="text-gray-300">{formatDate(change.opened_at)}</p>
              </div>
              <div>
                <span className="text-gray-500">Last Updated</span>
                <p className="text-gray-300">{formatDate(change.sys_updated_on)}</p>
              </div>
              <div>
                <span className="text-gray-500">Priority</span>
                <p className="text-gray-300">{change.priority || 'N/A'}</p>
              </div>
              {change.justification && (
                <div>
                  <span className="text-gray-500">Justification</span>
                  <p className="text-gray-300 text-xs mt-1">{change.justification}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const InfoItem = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
      <Icon size={12} />
      {label}
    </div>
    <p className="text-sm text-white truncate" title={value}>{value || 'N/A'}</p>
  </div>
);

const StatRow = ({ label, value, color }) => (
  <div className="flex justify-between">
    <span className="text-gray-400">{label}</span>
    <span className={color}>{value}</span>
  </div>
);

const CollapsibleSection = ({ title, icon: Icon, expanded, onToggle, children, highlight, badge }) => (
  <div className={`bg-dark-card rounded-lg border ${highlight === 'orange' ? 'border-orange-500/30' : 'border-dark-border'}`} style={{ background: '#0d0d0d' }}>
    <button
      onClick={onToggle}
      className="w-full p-4 flex items-center justify-between text-left hover:bg-dark-border/20 transition-colors"
    >
      <div className="flex items-center gap-2">
        <Icon size={16} className={highlight === 'orange' ? 'text-orange-accent' : 'text-purple-accent'} />
        <span className="font-semibold text-white">{title}</span>
        {badge && (
          <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/40">
            {badge}
          </span>
        )}
      </div>
      {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
    </button>
    {expanded && <div className="px-4 pb-4">{children}</div>}
  </div>
);

const CITable = ({ cis, getCIIcon, getEnvironmentColor, getConfidenceColor, getMatchStatusIcon }) => {
  if (!cis || cis.length === 0) {
    return <p className="text-gray-500 text-sm">No configuration items found.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-dark-border/50">
            <th className="text-left p-2 text-xs text-gray-500 uppercase">CI Name</th>
            <th className="text-left p-2 text-xs text-gray-500 uppercase">Type</th>
            <th className="text-left p-2 text-xs text-gray-500 uppercase">Environment</th>
            <th className="text-left p-2 text-xs text-gray-500 uppercase">Dynatrace Entity</th>
            <th className="text-center p-2 text-xs text-gray-500 uppercase">Confidence</th>
            <th className="text-center p-2 text-xs text-gray-500 uppercase">Status</th>
          </tr>
        </thead>
        <tbody>
          {cis.map((ci, idx) => (
            <tr key={ci.sys_id || idx} className="border-b border-dark-border/30 hover:bg-dark-border/20">
              <td className="p-2">
                <div className="flex items-center gap-2">
                  {getCIIcon(ci.sys_class_name)}
                  <div>
                    <p className="text-white font-medium">{ci.name || 'Unknown'}</p>
                    {ci.ip_address && <p className="text-xs text-gray-500 font-mono">{ci.ip_address}</p>}
                  </div>
                </div>
              </td>
              <td className="p-2 text-gray-400">{ci.sys_class_name || 'N/A'}</td>
              <td className="p-2">
                <span className={`px-2 py-0.5 text-xs rounded ${getEnvironmentColor(ci.u_environment)}`}>
                  {ci.u_environment || 'N/A'}
                </span>
              </td>
              <td className="p-2">
                {ci.entity_name || ci.entity_id ? (
                  <div>
                    <p className="text-cyan-accent">{ci.entity_name || ci.entity_id || 'Unknown Entity'}</p>
                    {ci.entity_type && <p className="text-xs text-gray-500">{ci.entity_type}</p>}
                    {ci.dynatrace_link && (
                      <a 
                        href={ci.dynatrace_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-cyan-accent/70 hover:text-cyan-accent flex items-center gap-1 mt-1"
                      >
                        <ExternalLink size={10} /> View in Dynatrace
                      </a>
                    )}
                  </div>
                ) : ci.match_result === 'MATCHED' || ci.match_result === 'CANDIDATES_FOUND' ? (
                  <div>
                    <p className="text-cyan-accent/70">Match found (details pending)</p>
                    <p className="text-xs text-gray-500">{ci.match_result}</p>
                  </div>
                ) : ci.match_result === 'NOT_MONITORABLE' ? (
                  <span className="text-gray-500">Not Monitorable</span>
                ) : ci.match_result === 'NO_MATCH' ? (
                  <span className="text-yellow-500">— No Match Found —</span>
                ) : (
                  <span className="text-gray-600">— Pending —</span>
                )}
              </td>
              <td className="p-2 text-center">
                {ci.confidence_score ? (
                  <span className={`font-semibold ${getConfidenceColor(ci.confidence_score)}`}>
                    {Math.round(ci.confidence_score * 100)}%
                  </span>
                ) : (
                  <span className="text-gray-600">—</span>
                )}
              </td>
              <td className="p-2 text-center">
                {getMatchStatusIcon(ci.match_result, ci.feedback_status)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InfraChangeDetail;
